import { englishError } from "./databaseErrors";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { businessDate } from "./stock";
import { refaqLogo, refaqLogoCss } from "./refaqBranding";
import { bodyFont, displayFont, invoiceTypography } from "./typography";
import { englishMaterialAlias, englishMaterialSnapshot } from "./materialNames";

export const PAYMENT_METHODS = { cash: "Cash", vodafone: "Vodafone Cash", insta: "InstaPay", cheque: "Cheque", bank: "Bank transfer" };
export const today = () => businessDate();
export const money = (value) => Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const cents = (value) => Math.round(Number(value || 0) * 100);
export function requestKey() {
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = [...bytes].map(b => b.toString(16).padStart(2,"0")).join("");
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}
async function allRows(table, showroomId = null, columns = "*", companyId = null) {
  const rows = [];
  for (let start = 0; ; start += 1000) {
    let query = supabase.from(table).select(columns).order("id").range(start,start+999);
    if (showroomId != null) query = query.eq("showroom_id",showroomId);
    if (companyId != null) query = query.eq("company_id",companyId);
    const { data, error } = await query;
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < 1000) return rows;
  }
}
export function buildSalesLedger(sales, lines, payments, returns, customers, showrooms, finished, materials = []) {
  return sales.map(sale => {
    const items = lines.filter(row => String(row.sale_id) === String(sale.id)).map(row => finished ? {
      ...row, materials: englishMaterialSnapshot(row.materials, materials),
      description: String(row.description || "").split(" / ").map(part => englishMaterialAlias(part, materials)).join(" / "),
    } : row);
    const receipts = payments.filter(row => String(row.sale_id) === String(sale.id));
    const credits = returns.filter(row => String(row.original_sale_id) === String(sale.id));
    const gross = cents(sale.gross_total ?? sale.total_amount ?? sale.total_price ?? items.reduce((sum,row) => sum+Number(row.total_price||0),0));
    const discount = cents(sale.discount_amount);
    const net = sale.net_total == null ? gross-discount : cents(sale.net_total);
    const returned = credits.reduce((sum,row) => sum+cents(row.total_refund),0);
    const paid = receipts.reduce((sum,row) => sum+cents(row.amount),0);
    const balance = net-returned-paid;
    return { ...sale, number: `${finished ? "RF" : "AH"}-${sale.id}`, customer: customers.find(c=>String(c.id)===String(sale.customer_id))?.name || "Unknown customer",
      showroom: showrooms.find(s=>String(s.id)===String(sale.showroom_id))?.name || "Unknown showroom", items, receipts, credits,
      gross:gross/100, discount:discount/100, returned:returned/100, net:(net-returned)/100, paid:paid/100, balance:balance/100,
      status: balance < 0 ? "Credit" : balance === 0 ? "Paid" : paid > 0 ? "Part paid" : "Unpaid" };
  }).sort((a,b)=>String(b.date).localeCompare(String(a.date)) || Number(b.id)-Number(a.id));
}
export function useSalesLedger(user, companyCode) {
  const [data,setData] = useState({ invoices:[], customers:[], showrooms:[], unallocated:0 });
  const [error,setError] = useState(""); const [loading,setLoading] = useState(true);
  const showroomId = user?.showroom_id, role = String(user?.role || "").toLowerCase();
  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const finished = companyCode === "refaq", all = ["owner","admin"].includes(role);
      if (!all && !showroomId) throw new Error("Assign a showroom to this employee to view sales.");
      const scope = all ? null : showroomId;
      const companyId = finished ? (await allRows("business_company",null,"id,code")).find(c=>c.code==="refaq")?.id : null;
      if (finished && !companyId) throw new Error("Refaq has not been configured in the database.");
      const [sales,lines,payments,returns,customers,showrooms,materials] = await Promise.all([
        allRows(finished ? "finished_sale" : "sale",scope,"*",companyId),
        allRows(finished ? "finished_sale_item" : "sale_details"),
        allRows(finished ? "finished_sale_payment" : "payment",finished ? null : scope),
        finished ? Promise.resolve([]) : allRows("return_note"),
        allRows("customer",null,"id,name,phone,roles"), allRows("showroom",null,"id,name"),
        finished ? allRows("material") : Promise.resolve([]),
      ]);
      const invoices = buildSalesLedger(sales,lines,payments,returns,customers,showrooms,finished,materials);
      const next = { invoices,customers,showrooms,unallocated:finished ? 0 : payments.filter(p=>p.sale_id==null).reduce((s,p)=>s+Number(p.amount),0) };
      setData(next); return next;
    } catch(err) { setError("Unable to load sales: "+englishError(err.message)); return null; }
    finally { setLoading(false); }
  },[companyCode,role,showroomId]);
  useEffect(()=>{reload();},[reload]);
  return {...data,loading,error,reload};
}
export const escapeHtml = value => String(value ?? "").replace(/[&<>"']/g,ch=>({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[ch]));
export function exportSales(invoices) {
  const cell = value => { let s=String(value??""); if (/^[\s]*[=+@-]/.test(s)) s="'"+s; return '"'+s.replace(/"/g,'""')+'"'; };
  const rows = [["Invoice","Date","Customer","Showroom","Net sales EGP","Paid EGP","Balance EGP","Status"],...invoices.map(i=>[i.number,String(i.date).slice(0,10),i.customer,i.showroom,i.net.toFixed(2),i.paid.toFixed(2),i.balance.toFixed(2),i.status])];
  const url=URL.createObjectURL(new Blob(["\uFEFF"+rows.map(r=>r.map(cell).join(",")).join("\r\n")],{type:"text/csv;charset=utf-8"}));
  const a=document.createElement("a"); a.href=url; a.download=`sales-${today()}.csv`; a.click(); URL.revokeObjectURL(url);
}
async function invoiceAsset(asset) {
  const response = await fetch(new URL(asset,window.location.href).href);
  if(!response.ok) throw new Error("Unable to load invoice branding.");
  const blob = await response.blob();
  return new Promise((resolve,reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
export function printFinishedInvoice(invoice) {
  const win=window.open("","_blank"); if(!win) return false;
  const e=escapeHtml;
  const render = (logoData,bodyData,displayData) => {
    if(win.closed) return;
    win.document.open();
    const logoHeader = logoData ? `<div class="refaq-logo-frame"><img src="${e(logoData)}" alt="Refaq logo"></div>` : "";
    win.document.write(`<!doctype html><html lang="en" dir="ltr"><head><title>${e(invoice.number)}</title><style>body{font:14px Arial;padding:32px;color:#514536}table{width:100%;border-collapse:collapse;margin:20px 0}th,td{padding:10px;border:1px solid #d2cbc1;text-align:left}h1{color:var(--accent, #514536)}${refaqLogoCss}${invoiceTypography(bodyData,displayData)}</style></head><body><header>${logoHeader}<h1>Refaq</h1><p>Marble Furniture and Accessories · Abbas Halim Group</p></header><h2>Invoice ${e(invoice.number)}</h2><p>Date: ${e(String(invoice.date).slice(0,10))} · Showroom: ${e(invoice.showroom)}</p><p>Customer: ${e(invoice.customer)}</p><table><thead><tr><th>Product / configuration</th><th>Size / materials</th><th>Pieces</th><th>Unit price EGP</th><th>Total EGP</th></tr></thead><tbody>${invoice.items.map(i=>`<tr><td>${e(i.sku)}<br>${e(i.description)}</td><td>${e(i.size_label)}<br>${e(i.materials)}</td><td>${e(i.quantity)}</td><td>${money(i.unit_price)}</td><td>${money(i.total_price)}</td></tr>`).join("")}</tbody></table><p>Gross: ${money(invoice.gross)} EGP · Discount: ${money(invoice.discount)} EGP</p><p><b>Net: ${money(invoice.net)} EGP · Paid: ${money(invoice.paid)} EGP · Balance: ${money(invoice.balance)} EGP</b></p><h3>Payments</h3>${invoice.receipts.map(p=>`<p>${e(p.payment_date)} · ${e(PAYMENT_METHODS[p.method]||p.method)} · ${money(p.amount)} EGP · ${e(p.reference)}</p>`).join("")||"<p>No payments recorded.</p>"}<p>Thank you for choosing Refaq.</p></body></html>`);
    win.document.close();
    const print = () => {
      const ready = win.document.fonts?.ready || Promise.resolve();
      Promise.race([ready, new Promise(resolve => setTimeout(resolve,2000))]).then(() => {
        if(!win.closed) { win.focus(); win.print(); }
      });
    };
    const image = win.document.querySelector(".refaq-logo-frame img");
    // A newly opened print window may need to fetch the logo before printing.
    if(!image || image.complete) print();
    else { image.addEventListener("load",print,{once:true}); image.addEventListener("error",print,{once:true}); }
  };
  win.document.write('<!doctype html><html lang="en"><head><title>Refaq invoice</title></head><body><p>Preparing invoice…</p></body></html>');
  win.document.close();
  // Keep the logo and font inside the print document so both render reliably.
  Promise.all([
    invoiceAsset(refaqLogo).catch(() => null),
    invoiceAsset(bodyFont).catch(() => null),
    invoiceAsset(displayFont).catch(() => null),
  ]).then(([logoData,bodyData,displayData]) => render(logoData,bodyData,displayData));
  return true;
}
